import { Box, Dialog, DialogContent, DialogTitle, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { MonitoredServer } from "@xop/dashboard-shared";
import type { JSX, ReactNode } from "react";

import type { ServerTone } from "../serverHealth.js";
import { onlinePill, onlineTone, toneColor } from "./serverPills.js";

/* ---------- Details modal ------------------------------------------ */

export function ServerDetailsModal({
  server,
  onClose,
}: {
  server: MonitoredServer;
  onClose: () => void;
}): JSX.Element {
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0.5 }}>
        {server.label}
        <Typography component="div" sx={{ mt: 0.25, fontSize: 12, color: "text.disabled" }}>
          {server.url}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
          <Section title="Reachability">
            <Row label="Status" value={onlinePill(server)} tone={onlineTone(server)} />
            <Row
              label="HTTP status"
              value={server.http_status != null ? String(server.http_status) : "—"}
            />
            <Row
              label="Latency"
              value={server.latency_ms != null ? `${server.latency_ms} ms` : "—"}
            />
            <Row
              label="Last checked"
              value={
                server.last_checked_at
                  ? new Date(server.last_checked_at).toLocaleString()
                  : "—"
              }
            />
            {server.last_error && (
              <Row label="Last error" value={server.last_error} tone="bad" wrap />
            )}
          </Section>

          <Section title="SSL certificate">
            {server.ssl ? (
              <>
                <Row
                  label="Valid"
                  value={server.ssl.valid ? "yes" : "no"}
                  tone={server.ssl.valid ? "good" : "bad"}
                />
                <Row label="Issuer" value={server.ssl.issuer ?? "—"} />
                <Row
                  label="Expires"
                  value={
                    server.ssl.expires_at
                      ? new Date(server.ssl.expires_at).toLocaleString()
                      : "—"
                  }
                />
                <Row
                  label="Days remaining"
                  value={
                    server.ssl.days_remaining != null
                      ? `${server.ssl.days_remaining}`
                      : "—"
                  }
                  tone={
                    server.ssl.days_remaining != null && server.ssl.days_remaining < 30
                      ? "warn"
                      : "good"
                  }
                />
              </>
            ) : (
              <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                No certificate data — probe didn't reach the TLS handshake or the
                URL isn't HTTPS.
              </Typography>
            )}
          </Section>

          <Section title="Site params">
            {server.params ? (
              <>
                <Row label="Version" value={server.params.version ?? "—"} />
                <Row label="Site name" value={server.params.site_name ?? "—"} />
                {server.params.dial_in_numbers.length > 0 && (
                  <Box sx={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 1.5, fontSize: 12 }}>
                    <Box component="span" sx={{ color: "text.disabled" }}>Dial-in</Box>
                    <Box component="ul" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, rowGap: 0.25, listStyle: "none", m: 0, p: 0 }}>
                      {server.params.dial_in_numbers.map((n) => (
                        <Box component="li" key={n} sx={{ fontFamily: "ui-monospace, monospace" }}>
                          {n}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
                {Object.keys(server.params.extra).length > 0 && (
                  <Box component="details" sx={{ mt: 0.5 }}>
                    <Box component="summary" sx={{ cursor: "pointer", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
                      Other params ({Object.keys(server.params.extra).length})
                    </Box>
                    <Box
                      component="pre"
                      sx={{
                        mt: 0.5,
                        maxHeight: 192,
                        overflow: "auto",
                        borderRadius: 0.5,
                        bgcolor: "background.default",
                        p: 1,
                        fontFamily: "ui-monospace, monospace",
                        fontSize: 10,
                        color: "text.secondary",
                      }}
                    >
                      {JSON.stringify(server.params.extra, null, 2)}
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                <Box component="code" sx={{ fontFamily: "ui-monospace, monospace" }}>/api/v2/get_site_params</Box> didn't
                return a JSON body — that's fine, it just means the upstream
                doesn't expose this endpoint.
              </Typography>
            )}
          </Section>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <Box component="section">
      <Typography
        component="h3"
        sx={{ mb: 1, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.disabled" }}
      >
        {title}
      </Typography>
      <Box component="dl" sx={{ display: "grid", gap: 0.75, m: 0 }}>{children}</Box>
    </Box>
  );
}

function Row({
  label,
  value,
  tone,
  wrap,
}: {
  label: string;
  value: string;
  tone?: ServerTone;
  wrap?: boolean;
}): JSX.Element {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 1.5, fontSize: 12 }}>
      <Box component="dt" sx={{ color: "text.disabled" }}>{label}</Box>
      <Box
        component="dd"
        sx={{
          m: 0,
          color: !tone || tone === "muted" ? "text.primary" : (t: Theme) => toneColor(t, tone),
          ...(wrap ? { wordBreak: "break-word" } : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }),
        }}
      >
        {value}
      </Box>
    </Box>
  );
}
